import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/environment';
import AccountInformationSection from '../components/AccountInformationSection';
import './SettingsPage.css';

const SettingsPage = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [activeTab, setActiveTab] = useState('account');
  
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });
  
  // Password Change State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Avatar State
  const [avatar, setAvatar] = useState(user?.customAvatar || null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);
  
  const [notifications, setNotifications] = useState({
    email: true,
    sms: false,
    push: true,
    orderUpdates: true,
    promotions: false
  });
  const [privacy, setPrivacy] = useState({
    showProfile: true,
    shareData: false,
    analytics: true
  });

  const handleNotificationChange = (key) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handlePrivacyChange = (key) => {
    setPrivacy(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Avatar Functions
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    if (file.size > 5 * 1024 * 1024) {
      setSaveMessage({ type: 'error', text: 'File size should be less than 5MB' });
      return;
    }

    if (!file.type.startsWith('image/')) {
      setSaveMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }

    try {
      setIsUploadingAvatar(true);
      setSaveMessage({ type: '', text: '' });

      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageUrl = e.target.result;
        
        // Update avatar in context
        if (updateProfile) {
          const result = await updateProfile({ customAvatar: imageUrl });
          if (result.success) {
            setAvatar(imageUrl);
            setSaveMessage({ type: 'success', text: '🖼️ Avatar updated successfully!' });
            
            // Clear success message after 3 seconds
            setTimeout(() => {
              setSaveMessage({ type: '', text: '' });
            }, 3000);
          } else {
            setSaveMessage({ type: 'error', text: result.error || 'Failed to update avatar' });
          }
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error updating avatar:', error);
      setSaveMessage({ type: 'error', text: 'Failed to update avatar' });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // Password Change Functions
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordUpdate = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSaveMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (!passwordData.currentPassword || !passwordData.newPassword) {
      setSaveMessage({ type: 'error', text: 'Please fill in all password fields' });
      return;
    }

    try {
      setIsChangingPassword(true);
      setSaveMessage({ type: '', text: '' });
      
      const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);
      
      if (result.success) {
        setSaveMessage({ type: 'success', text: '🔐 Password updated successfully! Your account is now more secure.' });
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSaveMessage({ type: '', text: '' });
        }, 5000);
      } else {
        setSaveMessage({ type: 'error', text: result.error || 'Password update failed. Please try again.' });
      }
    } catch (error) {
      console.error('Error updating password:', error);
      setSaveMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Account Deletion State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Account Deletion Functions
  const openDeleteAccountModal = () => {
    console.log('Opening delete modal...'); // Debug log
    console.log('Current state - showDeleteModal:', showDeleteModal); // Debug log
    setShowDeleteModal(true);
    setDeletionReason('');
    console.log('State updated - showDeleteModal should now be true'); // Debug log
  };

  const closeDeleteModal = () => {
    console.log('Closing delete modal...'); // Debug log
    setShowDeleteModal(false);
    setDeletionReason('');
  };

  const confirmDeleteAccount = async () => {
    console.log('confirmDeleteAccount called'); // Debug log
    try {
      setIsDeleting(true);
      console.log('Setting isDeleting to true'); // Debug log
      
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      console.log('Token found:', !!token); // Debug log
      
      if (!token) {
        console.log('No token found, showing error'); // Debug log
        setSaveMessage({ type: 'error', text: 'Authentication required. Please login again.' });
        return;
      }

      console.log('Making API call to delete account...'); // Debug log
      const response = await fetch(getApiUrl('/auth/delete-account'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reason: deletionReason
        })
      });

      console.log('API response status:', response.status); // Debug log
      const data = await response.json();
      console.log('API response data:', data); // Debug log
      
      if (data.success) {
        console.log('Account deletion successful'); // Debug log
        setSaveMessage({ type: 'success', text: 'Account deleted successfully. You can restore it within 30 days by contacting support.' });
        
        // Clear success message after 5 seconds and redirect
        setTimeout(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          localStorage.removeItem('sheenwear_user');
          window.location.href = '/login';
        }, 5000);
      } else {
        console.log('Account deletion failed:', data.message); // Debug log
        setSaveMessage({ type: 'error', text: data.message || 'Failed to delete account' });
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      setSaveMessage({ type: 'error', text: 'Failed to delete account' });
    } finally {
      console.log('Setting isDeleting to false'); // Debug log
      setIsDeleting(false);
      closeDeleteModal();
    }
  };

  const settingsTabs = [
    { id: 'account', label: 'Account', icon: '👤' },
    { id: 'security', label: 'Security', icon: '🔒' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'privacy', label: 'Privacy', icon: '🛡️' },
    { id: 'preferences', label: 'Preferences', icon: '⚙️' }
  ];

  return (
    <div className="settings-page">
      <div className="container">
        <div className="settings-header">
          <h1>Settings</h1>
          <p>Manage your account preferences and settings</p>
        </div>

        <div className="settings-content">
          {/* Settings Navigation */}
          <div className="settings-nav">
            {settingsTabs.map(tab => (
              <button
                key={tab.id}
                className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Settings Panels */}
          <div className="settings-panel">
            
            {/* Account Settings */}
            {activeTab === 'account' && (
              <div className="panel-content">
                <h2>Account Information</h2>
                <div className="setting-group">
                  {/* Avatar Section */}
                  <div className="setting-item avatar-section">
                    <label>Profile Picture</label>
                    <div className="avatar-container">
                      <div className="avatar-preview" onClick={handleAvatarClick}>
                        <img 
                          src={avatar || user?.avatar || '/default-avatar.png'} 
                          alt="Profile Avatar"
                          className="avatar-image"
                        />
                        <div className="avatar-overlay">
                          <span className="avatar-icon">📷</span>
                          <span className="avatar-text">
                            {isUploadingAvatar ? 'Uploading...' : 'Change Photo'}
                          </span>
                        </div>
                      </div>
                      <div className="avatar-actions">
                        <button 
                          type="button"
                          onClick={handleAvatarClick}
                          className="avatar-btn primary"
                          disabled={isUploadingAvatar}
                        >
                          📷 {isUploadingAvatar ? 'Uploading...' : 'Choose Photo'}
                        </button>
                        <p className="avatar-help">
                          Click to upload from gallery or take a photo
                        </p>
                      </div>
                    </div>
                  </div>

                  <AccountInformationSection />
                </div>
              </div>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <div className="panel-content">
                <h2>Security Settings</h2>
                
                {/* Save Message */}
                {saveMessage.text && (
                  <div className={`save-message ${saveMessage.type}`}>
                    {saveMessage.text}
                  </div>
                )}

                <div className="setting-group">
                  <div className="setting-item">
                    <label>Current Password</label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      placeholder="Enter your current password"
                      className="setting-input"
                    />
                  </div>
                  <div className="setting-item">
                    <label>New Password</label>
                    <input
                      type="password"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      placeholder="Enter your new password"
                      className="setting-input"
                    />
                  </div>
                  <div className="setting-item">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      placeholder="Confirm your new password"
                      className="setting-input"
                    />
                  </div>
                </div>
                
                <button 
                  onClick={handlePasswordUpdate}
                  className="save-btn"
                  disabled={isChangingPassword}
                >
                  {isChangingPassword ? 'Updating Password...' : 'Update Password'}
                </button>
              </div>
            )}

            {/* Notification Settings */}
            {activeTab === 'notifications' && (
              <div className="panel-content">
                <h2>Notification Preferences</h2>
                <div className="setting-group">
                  <div className="toggle-item">
                    <div className="toggle-info">
                      <h3>Email Notifications</h3>
                      <p>Receive notifications via email</p>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={notifications.email}
                        onChange={() => handleNotificationChange('email')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  
                  <div className="toggle-item">
                    <div className="toggle-info">
                      <h3>SMS Notifications</h3>
                      <p>Receive notifications via SMS</p>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={notifications.sms}
                        onChange={() => handleNotificationChange('sms')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  
                  <div className="toggle-item">
                    <div className="toggle-info">
                      <h3>Push Notifications</h3>
                      <p>Receive push notifications</p>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={notifications.push}
                        onChange={() => handleNotificationChange('push')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  
                  <div className="toggle-item">
                    <div className="toggle-info">
                      <h3>Order Updates</h3>
                      <p>Get notified about order status changes</p>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={notifications.orderUpdates}
                        onChange={() => handleNotificationChange('orderUpdates')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  
                  <div className="toggle-item">
                    <div className="toggle-info">
                      <h3>Promotional Offers</h3>
                      <p>Receive notifications about sales and offers</p>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={notifications.promotions}
                        onChange={() => handleNotificationChange('promotions')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Privacy Settings */}
            {activeTab === 'privacy' && (
              <div className="panel-content">
                <h2>Privacy & Security</h2>
                <div className="setting-group">
                  <div className="toggle-item">
                    <div className="toggle-info">
                      <h3>Public Profile</h3>
                      <p>Make your profile visible to other users</p>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={privacy.showProfile}
                        onChange={() => handlePrivacyChange('showProfile')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  
                  <div className="toggle-item">
                    <div className="toggle-info">
                      <h3>Data Sharing</h3>
                      <p>Allow sharing of anonymized data for improvements</p>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={privacy.shareData}
                        onChange={() => handlePrivacyChange('shareData')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  
                  <div className="toggle-item">
                    <div className="toggle-info">
                      <h3>Analytics</h3>
                      <p>Help improve our service with usage analytics</p>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={privacy.analytics}
                        onChange={() => handlePrivacyChange('analytics')}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
                
                <div className="danger-zone">
                  <h3>Danger Zone</h3>
                  <div className="danger-zone-content">
                    <div className="warning-box">
                      <h4>⚠️ Delete Account</h4>
                      <p>Once you delete your account, there is no going back. Please be certain.</p>
                      
                      <button 
                        onClick={openDeleteAccountModal} 
                        className="delete-account-btn"
                      >
                        Delete Account
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Preferences */}
            {activeTab === 'preferences' && (
              <div className="panel-content">
                <h2>App Preferences</h2>
                <div className="setting-group">
                  <div className="setting-item">
                    <label>Language</label>
                    <select className="setting-select">
                      <option value="en">English</option>
                      <option value="ur">Urdu</option>
                      <option value="hi">Hindi</option>
                    </select>
                  </div>
                  
                  <div className="setting-item">
                    <label>Currency</label>
                    <select className="setting-select">
                      <option value="PKR">Pakistani Rupee (PKR)</option>
                      <option value="USD">US Dollar (USD)</option>
                      <option value="EUR">Euro (EUR)</option>
                    </select>
                  </div>
                  
                  <div className="setting-item">
                    <label>Theme</label>
                    <select className="setting-select">
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                      <option value="auto">Auto</option>
                    </select>
                  </div>
                </div>
                <button className="save-btn">Save Preferences</button>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Delete Account Confirmation</h3>
            <p>Are you sure you want to delete your account? This action cannot be undone.</p>
            
            <div className="form-group">
              <label>Reason for deletion (optional):</label>
              <textarea 
                value={deletionReason} 
                onChange={(e) => setDeletionReason(e.target.value)}
                placeholder="Please tell us why you're leaving..."
                maxLength={500}
                className="deletion-reason-textarea"
              />
              <small className="char-count">{deletionReason.length}/500 characters</small>
            </div>
            
            <div className="modal-actions">
              <button onClick={closeDeleteModal} className="cancel-btn">
                Cancel
              </button>
              <button 
                onClick={confirmDeleteAccount} 
                className="confirm-delete-btn"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Yes, Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for avatar */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleAvatarChange}
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default SettingsPage; 